import { useCallback, useEffect, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { useNavigate } from "react-router-dom";
import { api, type SttJob } from "@/api/client";
import Toasts from "@/components/Toasts";
import SessionGuard from "@/components/SessionGuard";
import { PENDING_STT_MEETING_KEY } from "@/hooks/useChatStream";
import { toast } from "@/lib/toast";
import {
  ArrowLeft,
  AudioLines,
  Music,
  Download,
  Trash2,
  Copy,
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Clock,
  Users,
  NotebookPen,
} from "lucide-react";

// 받아쓰기(다글로 STT) — 음성 파일 → 텍스트 변환 페이지.
// 설계: ISAC_받아쓰기_다글로STT_설계서_v0.2.md §4.2 / 와이어프레임 화면 2~4.

const MAX_MB = 500; // 서버 STT_MAX_UPLOAD_MB 와 정합(1차 검증)
// 다글로 지원 포맷(설계서 §3.1)
const ALLOWED =
  /\.(3gp|3gpp|aac|ac3|aiff|amr|au|flac|m4a|mp3|mxf|opus|ra|wav|weba|asx|avi|ogm|ogv|m4v|mov|mp4|mpeg|mpg|wmv)$/i;
const ACCEPT =
  ".3gp,.3gpp,.aac,.ac3,.aiff,.amr,.au,.flac,.m4a,.mp3,.mxf,.opus,.ra,.wav,.weba,.asx,.avi,.ogm,.ogv,.m4v,.mov,.mp4,.mpeg,.mpg,.wmv";
const ACTIVE_STATUSES = new Set(["queued", "submitted", "processing"]);
const POLL_MS = 5000;

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function fmtSize(bytes: number | null): string {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function StatusBadge({ job }: { job: SttJob }) {
  if (job.status === "done")
    return (
      <span className="shrink-0 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700">
        완료
      </span>
    );
  if (job.status === "error")
    return (
      <span className="shrink-0 rounded-full border border-red-300 bg-red-50 px-2 py-0.5 text-[11px] text-red-700">
        실패
      </span>
    );
  if (job.status === "queued")
    return (
      <span className="shrink-0 rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
        대기중
      </span>
    );
  return (
    <span className="flex shrink-0 items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700">
      <Loader2 className="h-3 w-3 animate-spin" />
      변환중{job.progress > 0 ? ` ${job.progress}%` : ""}
    </span>
  );
}

export default function Stt() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<SttJob[]>([]);
  const [loading, setLoading] = useState(true);
  // 옵션 체크박스 2종 — 기본 모두 체크, 전송 후에도 유지(설계 §4.2)
  const [timestamps, setTimestamps] = useState(true);
  const [diarization, setDiarization] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  // 업로드 진행 상태(순차 업로드): 현재 파일명 + 진행률
  const [uploading, setUploading] = useState<{ name: string; pct: number } | null>(null);
  // 펼침 상태 + 전문 캐시(지연 로드)
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [textLoading, setTextLoading] = useState(false);
  // 삭제 확인 다이얼로그 대상
  const [confirmDelete, setConfirmDelete] = useState<SttJob | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async (silent = true) => {
    try {
      const r = await api.sttJobs();
      setJobs(r.items ?? []);
    } catch (e) {
      if (!silent) toast((e as Error).message || "목록을 불러오지 못했습니다.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  // 최초 로드
  useEffect(() => {
    refresh(false);
  }, [refresh]);

  // 진행 중 잡이 있을 때만 5초 폴링(없으면 중지, 페이지 이탈 시 정리 — 설계 §4.2)
  const hasActive = jobs.some((j) => ACTIVE_STATUSES.has(j.status));
  useEffect(() => {
    if (!hasActive) return;
    const t = window.setInterval(() => refresh(), POLL_MS);
    return () => window.clearInterval(t);
  }, [hasActive, refresh]);

  // ── 업로드(순차) ──
  const uploadFiles = async (files: File[]) => {
    for (const f of files) {
      if (!ALLOWED.test(f.name)) {
        toast(`지원하지 않는 형식: ${f.name}`, "error");
        continue;
      }
      if (f.size > MAX_MB * 1024 * 1024) {
        toast(`${f.name}: ${MAX_MB}MB 이하만 가능합니다.`, "error");
        continue;
      }
      setUploading({ name: f.name, pct: 0 });
      try {
        const job = await api.sttUpload(f, { timestamps, diarization }, (pct) =>
          setUploading({ name: f.name, pct }),
        );
        setJobs((prev) => [job, ...prev.filter((j) => j.id !== job.id)]);
        toast(`변환 요청 완료: ${f.name}`, "success");
      } catch (e) {
        toast((e as Error).message || `업로드 실패: ${f.name}`, "error");
      }
    }
    setUploading(null);
    refresh();
  };

  const onPickFile = (e: ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (list.length) void uploadFiles(list);
  };

  // ── 드래그앤드롭 ──
  const onDragOver = (e: DragEvent) => {
    if (uploading) return;
    if (Array.from(e.dataTransfer.types || []).includes("Files")) {
      e.preventDefault();
      setDragOver(true);
    }
  };
  const onDragLeave = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    const list = Array.from(e.dataTransfer.files ?? []);
    if (list.length) void uploadFiles(list);
  };

  // ── 내용 펼침(전문 지연 로드) ──
  const toggleExpand = async (job: SttJob) => {
    if (expandedId === job.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(job.id);
    if (texts[job.id] === undefined && job.status === "done") {
      setTextLoading(true);
      try {
        const d = await api.sttJob(job.id);
        setTexts((prev) => ({ ...prev, [job.id]: d.text || "" }));
      } catch {
        toast("내용을 불러오지 못했습니다.", "error");
        setExpandedId(null);
      } finally {
        setTextLoading(false);
      }
    }
  };

  const copyText = async (id: string) => {
    const t = texts[id];
    if (!t) return;
    try {
      await navigator.clipboard.writeText(t);
      toast("복사했습니다.", "success");
    } catch {
      toast("복사에 실패했습니다.", "error");
    }
  };

  // ── 회의록 작성 요청 — 잡 정보를 보관하고 채팅으로 이동 ──
  // Chat.tsx 가 마운트 시 PENDING_STT_MEETING_KEY 를 읽어 전문을 내려받고
  // meeting 스킬(회의록 작성)을 자동 실행한다.
  const requestMeeting = (job: SttJob) => {
    try {
      sessionStorage.setItem(
        PENDING_STT_MEETING_KEY,
        JSON.stringify({ jobId: job.id, filename: job.filename, ts: Date.now() }),
      );
    } catch {
      toast("요청을 준비하지 못했습니다. 다시 시도해 주세요.", "error");
      return;
    }
    navigate("/");
  };

  // ── 삭제(확인 다이얼로그 → 목록에서 제거) ──
  // 서버는 소프트 삭제(숨김) — 기록은 운영 관리용으로 보존되고 내 목록에서만 사라진다.
  const doDelete = async () => {
    const job = confirmDelete;
    if (!job) return;
    setConfirmDelete(null);
    try {
      await api.sttDelete(job.id);
      setJobs((prev) => prev.filter((j) => j.id !== job.id));
      if (expandedId === job.id) setExpandedId(null);
      toast("삭제했습니다.", "success");
    } catch (e) {
      toast((e as Error).message || "삭제에 실패했습니다.", "error");
    }
  };

  const cb =
    "flex cursor-pointer items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs text-foreground hover:bg-muted";

  return (
    <div className="flex h-full flex-col" onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
      <SessionGuard />
      {dragOver && (
        <div className="pointer-events-none fixed inset-0 z-20 m-2 flex items-center justify-center rounded-xl border-2 border-dashed border-primary bg-primary/5 text-sm font-medium text-primary">
          여기에 음성 파일을 놓으세요
        </div>
      )}

      {/* 삭제 확인 다이얼로그 — 프로젝트 변경 다이얼로그와 동일 패턴(와이어프레임 화면 4) */}
      {confirmDelete !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            className="w-full max-w-sm rounded-xl bg-card p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 font-semibold">변환 기록 삭제</div>
            <p className="text-sm text-muted-foreground">
              ‘{confirmDelete.filename}’의 변환 기록이 목록에서 삭제됩니다. 삭제하면 다시 볼 수
              없습니다.
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <button
                onClick={doDelete}
                className="w-full rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white"
              >
                삭제
              </button>
              <button
                onClick={() => setConfirmDelete(null)}
                className="w-full rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 헤더 — ← 뒤로가기(채팅 복귀) + 제목 */}
      <header className="sticky top-0 z-10 flex items-center gap-1 border-b border-border bg-background/90 px-2 py-2 backdrop-blur" style={{ paddingTop: "calc(0.5rem + env(safe-area-inset-top))" }}>
        <button
          onClick={() => navigate("/")}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground active:bg-muted"
          aria-label="채팅으로 돌아가기"
          title="채팅으로 돌아가기"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-1.5 font-bold">
          <AudioLines className="h-5 w-5 text-[#EB4E26]" />
          받아쓰기
        </div>
        <span className="ml-2 hidden text-xs text-muted-foreground sm:inline">
          음성 파일을 텍스트로 변환합니다
        </span>
      </header>

      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-3xl space-y-4 px-3 py-4">
          {/* 업로드 카드 */}
          <div className="rounded-xl border border-border bg-card p-3">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={!!uploading}
              className="flex w-full flex-col items-center gap-1 rounded-xl border-2 border-dashed border-border px-4 py-6 text-center hover:bg-muted disabled:opacity-50"
            >
              <Music className="h-7 w-7 text-muted-foreground" />
              <div className="text-sm font-medium">음성 파일을 끌어다 놓거나 클릭하여 선택</div>
              <div className="text-xs text-muted-foreground">
                mp3 · m4a · wav · mp4 등 / 최대 {MAX_MB}MB · 4시간
              </div>
            </button>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept={ACCEPT}
              className="hidden"
              onChange={onPickFile}
            />
            {/* 옵션 체크박스 2종 — 기본 모두 체크, 전송 후에도 유지 */}
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <label className={cb}>
                <input
                  type="checkbox"
                  checked={timestamps}
                  onChange={(e) => setTimestamps(e.target.checked)}
                  disabled={!!uploading}
                  className="h-3.5 w-3.5 accent-primary"
                />
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                타임스탬프 포함
              </label>
              <label className={cb}>
                <input
                  type="checkbox"
                  checked={diarization}
                  onChange={(e) => setDiarization(e.target.checked)}
                  disabled={!!uploading}
                  className="h-3.5 w-3.5 accent-primary"
                />
                <Users className="h-3.5 w-3.5 text-muted-foreground" />
                화자 분리
              </label>
            </div>
            {uploading && (
              <div className="mt-2.5">
                <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
                  <span className="truncate">업로드 중: {uploading.name}</span>
                  <span>{uploading.pct}%</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${uploading.pct}%` }}
                  />
                </div>
              </div>
            )}
            <p className="mt-2.5 text-[11px] leading-relaxed text-muted-foreground">
              ⓘ 음성 파일은 변환을 위해 다글로(액션파워) 서버로 전송됩니다. 대외비·고객 민감정보가
              포함된 녹음은 업로드 전 보안 지침을 확인하세요.
            </p>
          </div>

          {/* 변환 목록 */}
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold">변환 목록</div>
            <button
              onClick={() => refresh(false)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              title="새로고침 (진행 중이면 5초마다 자동 갱신)"
            >
              <RefreshCw className="h-3.5 w-3.5" /> 새로고침
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> 불러오는 중…
            </div>
          ) : jobs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border py-8 text-center text-sm text-muted-foreground">
              아직 변환한 파일이 없습니다. 음성 파일을 올려 시작해 보세요.
            </div>
          ) : (
            <ul className="space-y-2">
              {jobs.map((job) => {
                const expanded = expandedId === job.id;
                return (
                  <li key={job.id} className="rounded-xl border border-border bg-card p-3">
                    <div className="flex items-center gap-2">
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold">
                        {job.filename}
                      </span>
                      <StatusBadge job={job} />
                    </div>
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      요청 {fmtDate(job.created_at)} · 완료 {fmtDate(job.completed_at)}
                      {job.size_bytes ? ` · ${fmtSize(job.size_bytes)}` : ""}
                    </div>
                    {job.status === "error" && (
                      <div className="mt-1.5 text-xs text-red-600">
                        사유: {job.error_msg || "알 수 없는 오류"}
                      </div>
                    )}
                    {job.status === "done" && (
                      <button
                        onClick={() => toggleExpand(job)}
                        className="mt-1.5 w-full rounded-lg border border-border/60 bg-muted/50 px-2.5 py-1.5 text-left text-xs leading-relaxed text-muted-foreground hover:bg-muted"
                        title={expanded ? "접기" : "전체 내용 보기"}
                      >
                        {expanded ? (
                          <span className="flex items-center gap-1 font-medium text-foreground">
                            <ChevronUp className="h-3.5 w-3.5" /> 접기
                          </span>
                        ) : (
                          <>
                            {job.preview ? `${job.preview.slice(0, 100)}… ` : "내용 보기 "}
                            <span className="inline-flex items-center font-medium text-primary">
                              <ChevronDown className="h-3.5 w-3.5" /> 펼치기
                            </span>
                          </>
                        )}
                      </button>
                    )}
                    {expanded && job.status === "done" && (
                      <div className="mt-1.5">
                        {textLoading && texts[job.id] === undefined ? (
                          <div className="flex items-center gap-2 py-3 text-xs text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> 내용 불러오는 중…
                          </div>
                        ) : (
                          <pre className="max-h-72 overflow-y-auto whitespace-pre-wrap rounded-lg border border-border bg-background p-2.5 text-xs leading-relaxed">
                            {texts[job.id] || "(내용 없음)"}
                          </pre>
                        )}
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-1.5">
                      {expanded && job.status === "done" && texts[job.id] && (
                        <button
                          onClick={() => copyText(job.id)}
                          className="flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs hover:bg-muted"
                        >
                          <Copy className="h-3.5 w-3.5" /> 복사
                        </button>
                      )}
                      {job.status === "done" && (
                        <a
                          href={api.sttDownloadUrl(job.id)}
                          download
                          className="flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-muted"
                        >
                          <Download className="h-3.5 w-3.5" /> TXT 다운로드
                        </a>
                      )}
                      {job.status === "done" && (
                        <button
                          onClick={() => requestMeeting(job)}
                          className="flex items-center gap-1 rounded-lg border border-primary/40 px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-primary/5"
                          title="변환된 내용으로 ISAC에게 회의록 작성을 요청합니다"
                        >
                          <NotebookPen className="h-3.5 w-3.5" /> 회의록 작성 요청
                        </button>
                      )}
                      <button
                        onClick={() => setConfirmDelete(job)}
                        className="ml-auto flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 text-xs text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> 삭제
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </main>
      <Toasts />
    </div>
  );
}
